/**
 * PackingMaterialsTab — 5th tab in Inventory Dashboard.
 *
 * READ-ONLY view of packing material stock levels: item #, item name, current stock
 * (pieces on hand), safe qty and reorder qty. Same data and same API as the manager-only
 * Settings → Packing Materials tab (`GET /api/settings/packing-thresholds`), minus the
 * editing, adding and deleting.
 *
 * This replaced a packing-material *lots* table (`/lots?is_packing_material=Y`). Packing
 * stock lives in ics.hte_consumable_stock in PIECES (WCO-2155), not in ics.hte_lot, so
 * that table always rendered "No data available".
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Search, Loader2, AlertTriangle } from 'lucide-react';
import productionService from '../../services/productionService';
import { useLanguage } from '../../i18n/LanguageContext';
import { getPackingStockStatus, packingStockStatusColors } from '../../utils/packingStockStatus';
import { PackingQuantity } from '../../components/PackingQuantity';
import { usePackingStockLive } from '../../hooks/usePackingStockLive';

interface PackingStockRow {
  id: number;
  item_number: string;
  item_name: string | null;
  item_unit: string | null;
  item_sub_unit: string | null;
  safe_quantity: number;
  reorder_quantity: number;
  /** Pieces per bundle x safe_quantity, resolved server-side. Null = unresolvable. */
  safe_pieces: number | null;
  /** Pieces per bundle x reorder_quantity, resolved server-side. Null = unresolvable. */
  reorder_pieces: number | null;
  pieces_per_bundle: number | null;
  current_stock: number;
  /** On-hand expressed in bundles/cases (FLOOR), computed server-side. Null = unresolvable. */
  current_stock_bundles: number | null;
  current_stock_remainder: number | null;
}

const PackingMaterialsTab: React.FC = () => {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');

  // Live on-hand: refetch when stock is consumed, received or replenished.
  usePackingStockLive();

  // Same query key as PackingMaterialSettings so the WebSocket invalidation in
  // useDashboardWebSocket refreshes this view too when stock changes.
  const { data: rows = [], isLoading } = useQuery<PackingStockRow[]>(
    'packing-thresholds',
    () => productionService.getPackingThresholds(),
    { staleTime: 30_000, refetchInterval: 30_000 }
  );

  // Client-side filter — the endpoint returns every configured material in one shot.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.item_number.toLowerCase().includes(q) ||
      (r.item_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <>
      {/* Search */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input
            type="text"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-secondary-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          {t('common.loading')}
        </div>
      ) : filteredRows.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t('admin.packingMaterials.itemNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t('admin.packingMaterials.itemName')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t('admin.packingMaterials.currentStock')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t('admin.packingMaterials.safeQuantity')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t('admin.packingMaterials.reorderQuantity')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredRows.map((row) => {
                  const status = getPackingStockStatus(row);
                  return (
                    <tr key={row.item_number} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-mono font-medium text-gray-900 dark:text-white">
                        {row.item_number}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {row.item_name || '-'}
                      </td>
                      {/* All three columns read in the SAME denomination (BD/CS) so they can be
                          compared at a glance; the exact piece count lives in the ⓘ tooltip. */}
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${packingStockStatusColors[status]}`}>
                        <div className="flex items-center justify-end space-x-1">
                          {(status === 'low' || status === 'critical') && <AlertTriangle className="w-3.5 h-3.5" />}
                          <PackingQuantity
                            bundles={row.current_stock_bundles}
                            pieces={row.current_stock}
                            unit={row.item_unit}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300">
                        <div className="flex items-center justify-end">
                          <PackingQuantity
                            bundles={row.safe_quantity}
                            pieces={row.safe_pieces}
                            unit={row.item_unit}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300">
                        <div className="flex items-center justify-end">
                          <PackingQuantity
                            bundles={row.reorder_quantity}
                            pieces={row.reorder_pieces}
                            unit={row.item_unit}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          {rows.length === 0 ? t('admin.packingMaterials.noThresholds') : t('messages.noData')}
        </div>
      )}
    </>
  );
};

export default PackingMaterialsTab;
